import { useLayoutEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { IconButton } from "@/components/common/IconButton";
import { captionStyle } from "@/components/infra/infra-styles";
import { INPUT_STYLE, focusRingOff, focusRingOn } from "@/lib/form-styles";
import { dateFormatter } from "@/lib/format-datetime";
import { niceTrendMax } from "@/lib/infra-performance";

const PLOT_HEIGHT = 96;
/** 左は目盛の数値 (例: 1.25 秒) が収まる幅。 */
const MARGIN = { top: 6, right: 8, bottom: 18, left: 60 };
/** 目盛の日付ラベル 1 つが使う幅の目安 (px)。 */
const X_TICK_SPACING = 72;

const tickFormat = new Intl.DateTimeFormat("ja-JP", { month: "numeric", day: "numeric" });
/** バックエンドの PerformanceAnnotationStore.MaxTextLength と同じ。 */
const ANNOTATION_MAX_LENGTH = 200;

export interface TrendPoint {
	start: string;
	end: string;
}

/** 点ごとに使われていたクエリの版。版ごとに背景を分け、押した版を選ぶ。 */
export interface TrendVersions {
	/** 点ごとの版の ID。版が分からない点は null。 */
	ids: (string | null)[];
	labels: ReadonlyMap<string, string>;
	selected: string | null;
	onSelect: (id: string) => void;
}

export interface TrendAnnotation {
	id: string;
	at: string;
	text: string;
	createdBy: string | null;
}

/** 全てのグラフに出す目印。グラフを押すと、その時刻の近くに目印を付けたり消したりできる。 */
export interface TrendAnnotations {
	items: TrendAnnotation[];
	onAdd: (at: string, text: string) => Promise<void>;
	onDelete: (id: string) => Promise<void>;
}

export interface TrendSeries<P extends TrendPoint> {
	label: string;
	color: string;
	/** 値が無い区間は null。線を途切れさせる。 */
	value: (point: P) => number | null;
}

/**
 * パフォーマンスタブの推移の折れ線グラフ。点は区間の始まりの時刻に置き、ポインタを乗せると日付と各系列の値を見せる。
 */
export function PerformanceTrendChart<P extends TrendPoint>({
	title,
	points,
	series,
	format,
	testId,
	versions,
	annotations,
}: {
	title: string;
	points: P[];
	series: TrendSeries<P>[];
	format: (value: number) => string;
	testId: string;
	versions?: TrendVersions;
	annotations?: TrendAnnotations;
}) {
	const containerRef = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(0);
	const [hoverIndex, setHoverIndex] = useState<number | null>(null);
	const [draftAt, setDraftAt] = useState<number | null>(null);

	useLayoutEffect(() => {
		const el = containerRef.current;
		if (!el) return;
		const observer = new ResizeObserver((entries) => setWidth(entries[0].contentRect.width));
		observer.observe(el);
		return () => observer.disconnect();
	}, []);

	const plotWidth = Math.max(width - MARGIN.left - MARGIN.right, 0);
	const times = points.map((p) => Date.parse(p.start));
	const first = times[0] ?? 0;
	const span = (times.at(-1) ?? 0) - first;
	const xAt = (i: number) => (span > 0 ? ((times[i] - first) / span) * plotWidth : plotWidth / 2);
	const lastPoint = points.at(-1);
	const lastEnd = lastPoint === undefined ? 0 : Date.parse(lastPoint.end);
	const xOfTime = (t: number) =>
		span > 0 ? Math.min(((t - first) / span) * plotWidth, plotWidth) : plotWidth / 2;
	const timeOfX = (px: number) =>
		span > 0 ? first + (Math.max(0, Math.min(px, plotWidth)) / plotWidth) * span : first;
	/** 1 区間の長さ。押した時刻のこれの半分以内にある目印を「近く」とみなす。 */
	const stepMs = points.length > 1 ? span / (points.length - 1) : 60 * 60 * 1000;
	const shownAnnotations = (annotations?.items ?? []).filter((a) => {
		const t = Date.parse(a.at);
		return t >= first && t <= lastEnd;
	});
	const values = points.flatMap((p) => series.map((s) => s.value(p) ?? 0));
	const yMax = niceTrendMax(Math.max(0, ...values));
	const yAt = (value: number) => PLOT_HEIGHT - (value / yMax) * PLOT_HEIGHT;
	const yTicks = [0, yMax / 2, yMax];

	const tickCount = Math.max(Math.min(Math.floor(plotWidth / X_TICK_SPACING), points.length), 1);
	// 1 時間ごとの点は同じ日付が続くため、同じ日付の目盛は最初の 1 つだけ残す。
	const xTickIndexes = Array.from({ length: tickCount }, (_, i) =>
		tickCount === 1 ? 0 : Math.round((i / (tickCount - 1)) * (points.length - 1)),
	).filter(
		(index, i, all) =>
			i === 0 || tickFormat.format(times[index]) !== tickFormat.format(times[all[i - 1]]),
	);

	const hovered = hoverIndex === null ? undefined : points.at(hoverIndex);
	const indexAt = (e: PointerEvent<SVGSVGElement> | MouseEvent<SVGSVGElement>) =>
		nearestIndex(
			points.length,
			xAt,
			e.clientX - e.currentTarget.getBoundingClientRect().left - MARGIN.left,
		);
	const hoveredVersion = hoverIndex === null ? null : (versions?.ids[hoverIndex] ?? null);
	const clickable = versions !== undefined || annotations !== undefined;

	return (
		<div data-testid={testId} style={{ flex: "1 1 320px", minWidth: 0 }}>
			<div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
				<span style={captionStyle({ fontWeight: 600, color: "var(--fg-2)" })}>{title}</span>
				{series.length > 1 &&
					series.map((s) => (
						<span
							key={s.label}
							style={captionStyle({ display: "flex", alignItems: "center", gap: 4 })}
						>
							<span style={{ width: 10, height: 2, background: s.color }} />
							{s.label}
						</span>
					))}
			</div>
			<div ref={containerRef} style={{ position: "relative", width: "100%" }}>
				{width > 0 && points.length > 0 && (
					<svg
						width={width}
						height={MARGIN.top + PLOT_HEIGHT + MARGIN.bottom}
						style={{ display: "block", cursor: clickable ? "pointer" : undefined }}
						onPointerMove={(e) => setHoverIndex(indexAt(e))}
						onPointerLeave={() => setHoverIndex(null)}
						onClick={(e) => {
							// 別の版を押したら版を選ぶだけにし、選んでいる版を押したときに目印の入力欄を開く。
							const id = versions?.ids[indexAt(e)];
							if (versions && id && id !== versions.selected) {
								versions.onSelect(id);
								return;
							}
							if (annotations)
								setDraftAt(
									timeOfX(e.clientX - e.currentTarget.getBoundingClientRect().left - MARGIN.left),
								);
						}}
					>
						<g transform={`translate(${MARGIN.left},${MARGIN.top})`}>
							{versions && <VersionBands versions={versions} xAt={xAt} plotWidth={plotWidth} />}
							{yTicks.map((tick) => (
								<g key={tick}>
									<line
										x1={0}
										x2={plotWidth}
										y1={yAt(tick)}
										y2={yAt(tick)}
										stroke={tick === 0 ? "var(--fg-4)" : "var(--line-1)"}
									/>
									<text
										x={-8}
										y={yAt(tick)}
										textAnchor="end"
										dominantBaseline="middle"
										style={{
											fontSize: "var(--text-section)",
											fill: "var(--fg-3)",
											fontVariantNumeric: "tabular-nums",
										}}
									>
										{format(tick)}
									</text>
								</g>
							))}
							{xTickIndexes.map((i) => (
								<text
									key={i}
									x={xAt(i)}
									y={PLOT_HEIGHT + 14}
									textAnchor={
										points.length === 1
											? "middle"
											: i === 0
												? "start"
												: i === points.length - 1
													? "end"
													: "middle"
									}
									style={{ fontSize: "var(--text-section)", fill: "var(--fg-3)" }}
								>
									{tickFormat.format(times[i])}
								</text>
							))}
							{hoverIndex !== null && (
								<line
									x1={xAt(hoverIndex)}
									x2={xAt(hoverIndex)}
									y1={0}
									y2={PLOT_HEIGHT}
									stroke="var(--fg-4)"
								/>
							)}
							{shownAnnotations.map((a) => (
								<AnnotationLine key={a.id} annotation={a} x={xOfTime(Date.parse(a.at))} />
							))}
							{series.map((s) => (
								<SeriesLine
									key={s.label}
									values={points.map(s.value)}
									color={s.color}
									xAt={xAt}
									yAt={yAt}
								/>
							))}
						</g>
					</svg>
				)}
				{annotations && draftAt !== null && (
					<AnnotationPopover
						at={draftAt}
						nearby={annotations.items.filter(
							(a) => Math.abs(Date.parse(a.at) - draftAt) <= stepMs / 2,
						)}
						onAdd={annotations.onAdd}
						onDelete={annotations.onDelete}
						onClose={() => setDraftAt(null)}
						x={MARGIN.left + xOfTime(draftAt)}
						containerWidth={width}
					/>
				)}
				{draftAt === null && hovered !== undefined && hoverIndex !== null && (
					<TrendTooltip
						point={hovered}
						series={series}
						format={format}
						versionLabel={
							hoveredVersion === null ? undefined : versions?.labels.get(hoveredVersion)
						}
						x={MARGIN.left + xAt(hoverIndex)}
						containerWidth={width}
					/>
				)}
			</div>
		</div>
	);
}

function describeAnnotation(a: TrendAnnotation): string {
	const by = a.createdBy === null ? "" : ` · ${a.createdBy}`;
	return `${a.text} (${dateFormatter.format(new Date(a.at))}${by})`;
}

/** 目印の縦線。上端の丸にポインタを乗せると内容を見せる。 */
function AnnotationLine({ annotation, x }: { annotation: TrendAnnotation; x: number }) {
	return (
		<g data-testid={`performance-annotation-${annotation.id}`}>
			<line x1={x} x2={x} y1={0} y2={PLOT_HEIGHT} stroke="var(--warning)" strokeWidth={1.5} />
			<circle cx={x} cy={0} r={3} fill="var(--warning)">
				<title>{describeAnnotation(annotation)}</title>
			</circle>
		</g>
	);
}

/** グラフを押した時刻に目印を付ける入力欄。その時刻の近くにある目印はここから消せる。 */
function AnnotationPopover({
	at,
	nearby,
	onAdd,
	onDelete,
	onClose,
	x,
	containerWidth,
}: {
	at: number;
	nearby: TrendAnnotation[];
	onAdd: (at: string, text: string) => Promise<void>;
	onDelete: (id: string) => Promise<void>;
	onClose: () => void;
	x: number;
	containerWidth: number;
}) {
	const [text, setText] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const trimmed = text.trim();
	// 失敗は入力欄の下に見せてから投げ直し、押した側で記録する。
	const run = async (operation: () => Promise<void>, failure: string) => {
		setBusy(true);
		setError(null);
		try {
			await operation();
		} catch (err) {
			setError(err instanceof Error ? err.message : failure);
			throw err;
		} finally {
			setBusy(false);
		}
	};
	const add = () => {
		run(async () => {
			await onAdd(new Date(at).toISOString(), trimmed);
			onClose();
		}, "目印を付けられませんでした。").catch((err: unknown) =>
			console.error("目印を付けられませんでした。", err),
		);
	};
	const remove = (id: string) => {
		run(() => onDelete(id), "目印を消せませんでした。").catch((err: unknown) =>
			console.error("目印を消せませんでした。", err),
		);
	};
	// グラフの右半分では左側へ出し、はみ出しを避ける。
	const flip = x > containerWidth / 2;
	const canAdd = trimmed !== "" && !busy;

	return (
		<div
			data-testid="performance-annotation-popover"
			style={{
				position: "absolute",
				top: MARGIN.top,
				left: flip ? undefined : x + 8,
				right: flip ? containerWidth - x + 8 : undefined,
				zIndex: 20,
				width: 260,
				padding: 8,
				background: "var(--surface-1)",
				borderRadius: "var(--radius-6)",
				boxShadow: "var(--shadow-menu)",
			}}
		>
			<div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
				<span style={captionStyle({ marginRight: "auto" })}>
					{dateFormatter.format(new Date(at))} に目印を付ける
				</span>
				<IconButton icon="x" iconSize={14} size={20} onClick={onClose} />
			</div>
			{nearby.map((a) => (
				<div
					key={a.id}
					style={captionStyle({ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 })}
				>
					<span style={{ flex: 1, minWidth: 0, color: "var(--fg-1)", wordBreak: "break-all" }}>
						{describeAnnotation(a)}
					</span>
					<IconButton
						icon="trash"
						iconSize={14}
						size={20}
						hoverColor="var(--danger)"
						title="この目印を消す"
						data-testid={`performance-annotation-delete-${a.id}`}
						disabled={busy}
						onClick={() => remove(a.id)}
					/>
				</div>
			))}
			<form
				style={{ display: "flex", gap: 6 }}
				onSubmit={(e) => {
					e.preventDefault();
					if (canAdd) add();
				}}
			>
				<input
					autoFocus
					data-testid="performance-annotation-text"
					value={text}
					maxLength={ANNOTATION_MAX_LENGTH}
					placeholder="例: 枯渇スイープを範囲走査に変更"
					onChange={(e) => setText(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === "Escape") onClose();
					}}
					style={{ ...INPUT_STYLE, height: 28, fontSize: "var(--text-caption)" }}
					onFocus={(e) => focusRingOn(e.currentTarget)}
					onBlur={(e) => focusRingOff(e.currentTarget)}
				/>
				<span style={{ display: "flex", flexShrink: 0 }}>
					<ConfirmButton
						type="submit"
						label="付ける"
						size="sm"
						loading={false}
						disabled={!canAdd}
						data-testid="performance-annotation-add"
					/>
				</span>
			</form>
			{error !== null && (
				<p style={captionStyle({ margin: "6px 0 0", color: "var(--danger)" })}>{error}</p>
			)}
		</div>
	);
}

/** 同じ版が続く点の範囲。 */
function versionRuns(ids: (string | null)[]) {
	const runs: { id: string; first: number; last: number }[] = [];
	for (const [i, id] of ids.entries()) {
		if (id === null) continue;
		const last = runs.at(-1);
		if (last?.id === id && last.last === i - 1) last.last = i;
		else runs.push({ id, first: i, last: i });
	}
	return runs;
}

/** 版ごとの背景。選んだ版を塗り、版の変わり目に破線を引く。 */
function VersionBands({
	versions,
	xAt,
	plotWidth,
}: {
	versions: TrendVersions;
	xAt: (i: number) => number;
	plotWidth: number;
}) {
	const lastIndex = versions.ids.length - 1;
	return (
		<g>
			{versionRuns(versions.ids).map((run) => {
				const left = run.first === 0 ? 0 : (xAt(run.first - 1) + xAt(run.first)) / 2;
				const right = run.last === lastIndex ? plotWidth : (xAt(run.last) + xAt(run.last + 1)) / 2;
				const selected = run.id === versions.selected;
				return (
					<g key={run.first} data-testid={`performance-trend-version-${run.id}`}>
						<rect
							x={left}
							y={0}
							width={Math.max(right - left, 0)}
							height={PLOT_HEIGHT}
							fill={selected ? "var(--accent-subtle)" : "transparent"}
						/>
						{run.first > 0 && (
							<line
								x1={left}
								x2={left}
								y1={0}
								y2={PLOT_HEIGHT}
								stroke="var(--accent-line)"
								strokeDasharray="3 3"
							/>
						)}
						{right - left > 32 && (
							<text
								x={left + 4}
								y={10}
								style={{
									fontSize: "var(--text-section)",
									fill: selected ? "var(--accent)" : "var(--fg-3)",
								}}
							>
								{versions.labels.get(run.id)}
							</text>
						)}
					</g>
				);
			})}
		</g>
	);
}

/** 値の続く区間を折れ線で結ぶ。前後が欠けて線にならない点は丸で見せる。 */
function SeriesLine({
	values,
	color,
	xAt,
	yAt,
}: {
	values: (number | null)[];
	color: string;
	xAt: (i: number) => number;
	yAt: (value: number) => number;
}) {
	const path = values
		.map((v, i) => {
			if (v === null) return "";
			return `${i > 0 && values[i - 1] !== null ? "L" : "M"}${xAt(i)},${yAt(v)}`;
		})
		.join(" ");
	const isolated = values.flatMap((v, i) =>
		v !== null && (values[i - 1] ?? null) === null && (values[i + 1] ?? null) === null
			? [{ i, v }]
			: [],
	);
	return (
		<g>
			<path
				d={path}
				fill="none"
				stroke={color}
				strokeWidth={1.5}
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			{isolated.map(({ i, v }) => (
				<circle key={i} cx={xAt(i)} cy={yAt(v)} r={2} fill={color} />
			))}
		</g>
	);
}

function nearestIndex(count: number, xAt: (i: number) => number, px: number): number {
	let nearest = 0;
	for (let i = 1; i < count; i++) {
		if (Math.abs(xAt(i) - px) < Math.abs(xAt(nearest) - px)) nearest = i;
	}
	return nearest;
}

function TrendTooltip<P extends TrendPoint>({
	point,
	series,
	format,
	versionLabel,
	x,
	containerWidth,
}: {
	point: P;
	series: TrendSeries<P>[];
	format: (value: number) => string;
	versionLabel?: string;
	x: number;
	containerWidth: number;
}) {
	// グラフの右半分では左側へ出し、はみ出しを避ける。
	const flip = x > containerWidth / 2;
	return (
		<div
			data-testid="performance-trend-tooltip"
			style={captionStyle({
				position: "absolute",
				top: 0,
				left: flip ? undefined : x + 12,
				right: flip ? containerWidth - x + 12 : undefined,
				zIndex: 10,
				padding: "6px 8px",
				whiteSpace: "nowrap",
				background: "var(--surface-1)",
				boxShadow: "var(--shadow-ring)",
				borderRadius: "var(--radius-3)",
				pointerEvents: "none",
			})}
		>
			<div style={{ marginBottom: 4 }}>
				{dateFormatter.format(new Date(point.start))}
				{versionLabel !== undefined && ` · ${versionLabel}`}
			</div>
			{series.map((s) => {
				const value = s.value(point);
				return (
					<div key={s.label} style={{ display: "flex", gap: 12 }}>
						<span style={{ marginRight: "auto", color: "var(--fg-2)" }}>{s.label}</span>
						<span
							style={{ fontWeight: 600, color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}
						>
							{value === null ? "—" : format(value)}
						</span>
					</div>
				);
			})}
		</div>
	);
}
